/**
 * Pipe-change schedule and events.
 *
 * A `PipeChange` is one row in the pipe schedule — one pipe, numbered
 * from 1. Detection is data-driven (bypass-valve status + RPM/advance
 * rate, see apps/backend/src/pipeChanges/detector.ts). The operator
 * may then edit rows; edited schedules are persisted and used verbatim
 * until the operator hits Reset.
 */

export interface PipeChange {
  /** 1-based pipe number. */
  pipeIndex: number;
  /** Operator-set display name. Null = use the default ("Pipe N"). */
  name: string | null;
  /** Install chainage (m) — where this pipe's leading edge entered the ground. */
  chainageM: number;
  /** Length (m). For an in-progress pipe this is 0 until the next install. */
  lengthM: number;
  /** Push start — first ts where all activity params are non-zero. Null if not started. */
  startTs: string | null;
  /** Push end — last ts where all activity params are non-zero before the next install. Null if in progress. */
  endTs: string | null;
  /**
   * Per-pipe override for ram-stage correction. The Ram corr / Stage 1
   * columns only appear in the editor when the GLOBAL
   * RamStageCorrection.enabled is true; this field lets the operator
   * skip correction for an individual pipe (e.g. an interjack where
   * the multistage ram doesn't behave the same way) without touching
   * the global toggle.
   *
   *   null / undefined → apply (the default when the global is on),
   *   true             → apply,
   *   false            → skip this pipe even when the global is on.
   */
  ramCorrectionEnabled?: boolean | null;
  /**
   * Per-pipe override for the stage-1 length (m). Null/undefined means
   * "use the global RamStageCorrection.stage1LengthM"; an explicit
   * number wins. Stored per-pipe so an operator who tunes one pipe's
   * stage 1 length doesn't have their value silently displaced if the
   * global default later changes.
   */
  ramStage1LengthM?: number | null;
}

/**
 * Operator-tunable knob for the pipe-change detector, editable at the
 * top of the Pipe Schedule page.
 */
export interface PipeDetectionConfig {
  /** Minimum chainage advance (m) between consecutive installs. */
  minAdvanceM: number;
}

/**
 * Ribbon payload describing pipe activity.
 *
 * `pipesToday` and `pipesPreviousShift` are *fractional* — chainage
 * advance in the window divided by the active pipe length at the
 * current chainage. An operator who advanced 5.2 m today on a 4 m
 * pipe drive sees "1.3 pipes today", which matches their mental model
 * of progress more naturally than a count-of-crossings would.
 *
 * `ready` is false until enough state exists to compute the metrics —
 * specifically: at least one pipe item in the schedule and a chainage
 * signal in the readings table. When false, the dashboard tiles render
 * "Initialising…" rather than zeros.
 */
export interface PipeMetrics {
  /** False when the schedule is empty or the active pipe length is unknown. */
  ready: boolean;
  /** Fractional pipes advanced today (todayAdvanceM ÷ active pipe length). */
  pipesToday: number;
  /** Fractional pipes advanced during the previous shift window. */
  pipesPreviousShift: number;
  /** True when no pipe is being pushed — a pipe change (install) is under way. */
  betweenPipes: boolean;
  /** 1-based index of the pipe currently being pushed, or null if none. */
  currentPipeIndex: number | null;
  /**
   * ISO 8601 — when the current pipe's push began, or (between pipes)
   * when the last pipe ended. With no pipe on this drive yet it falls
   * back to the drive start date, so the ribbon's elapsed counter has a
   * meaningful anchor from the first moment of a drive.
   */
  currentPipeStartTs: string | null;
  /** Minutes to push the most recently completed pipe. Null until one completes. */
  lastPipeDurationMin: number | null;
}

/**
 * Ram-stage correction for multistage jacking rams. When enabled, the
 * Jacking Force is multiplied by a stage-dependent ratio: within each
 * pipe push the first `stage1LengthM` (the ram's stage 1) by
 * `stage1Ratio`, the remainder by `laterStageRatio`. Applied to the
 * Drive parameters plot and fed to the JF prediction model as the
 * corrected measured Jacking Force. Persisted per-drive (see
 * apps/backend/src/routes/drive.ts).
 */
export interface RamStageCorrection {
  enabled: boolean;
  /** Stage-1 ram length (m), measured from each pipe push's start chainage. */
  stage1LengthM: number;
  /** Multiplier applied to JF within the stage-1 length. */
  stage1Ratio: number;
  /** Multiplier applied to JF beyond the stage-1 length. */
  laterStageRatio: number;
}

export const DEFAULT_RAM_STAGE_CORRECTION: RamStageCorrection = {
  enabled: false,
  stage1LengthM: 1.665,
  stage1Ratio: 1.54,
  laterStageRatio: 1,
};

/**
 * Ram-stage correction ratio for a Jacking Force sample at `chainageM`.
 * Within each pipe push the first `stage1LengthM` is the ram's stage 1
 * (ratio `stage1Ratio`); the rest is later stages (ratio
 * `laterStageRatio`). Returns 1 when the correction is disabled, the
 * chainage is unknown, or `chainageM` falls outside any pipe push — no
 * correction. `pipeChanges` must be ordered by install chainage.
 *
 * INVARIANT: this function is the ONLY way ram-stage correction reaches
 * a JF sample. It is applied at exactly four sites — once per
 * independent JF pipeline, never twice on the same input:
 *
 *   1. apps/backend/src/jackingForce/recompute.ts  (raw JF -> sidecar
 *      input; sidecar echoes back as payload.measured, which the JF
 *      page plots directly — no second multiplication).
 *   2. apps/backend/src/routes/secondary.ts        (raw JF -> Skin
 *      Friction analysis; gated by analysis === "skin_friction").
 *   3. apps/frontend/src/pages/DriveParameters.tsx (raw JF from
 *      /api/series -> render-time multiply on the Jacking Force
 *      trace; /api/series itself does NOT apply ram-stage).
 *   4. apps/backend/src/routes/latest.ts           (rolling-filtered
 *      raw JF -> dashboard live value + sparkline; the frontend shows
 *      /api/latest as-is).
 *
 * Adding a fourth caller is fine; re-multiplying any JF value that has
 * already passed through one of these sites is a bug.
 */
export function ramStageRatio(
  chainageM: number | null,
  pipeChanges: PipeChange[],
  cfg: RamStageCorrection,
): number {
  if (!cfg.enabled || chainageM == null) return 1;
  for (let i = 0; i < pipeChanges.length; i++) {
    const pc = pipeChanges[i]!;
    const start = pc.chainageM;
    const end = pipeChanges[i + 1]?.chainageM ?? Infinity;
    if (chainageM >= start && chainageM < end) {
      // Per-pipe opt-out: explicit `false` means the operator unticked
      // ram correction for this pipe in the schedule editor (e.g. an
      // interjack). Null/undefined falls through to the global default,
      // which is what existing rows + auto-detected pipes carry.
      if (pc.ramCorrectionEnabled === false) return 1;
      // Per-pipe stage-1 length: an explicit number overrides the
      // global. Falling back to `cfg.stage1LengthM` keeps schedules
      // saved before this feature behaving exactly as they did.
      const stage1Len = pc.ramStage1LengthM ?? cfg.stage1LengthM;
      return chainageM < start + stage1Len
        ? cfg.stage1Ratio
        : cfg.laterStageRatio;
    }
  }
  return 1;
}
